import Decimal from 'decimal.js'

import { SimulateOrdersResponse } from '@enechain/eposition-proto/bffportal/v1/simulation_service_pb'
import { OrderFeeResult } from '@enechain/eposition-proto/devtool/v1/service_pb'
import { DealPosition } from '@enechain/eposition-proto/types/v1/deal_pb'
import { createOrderSummaryText } from '@enechain/eposition-ui'

import { SimulationOrderPatternModel } from '~/pages/Simulation/models/SimulationOrderPattern'
import { TransactionSchema } from '~/pages/Simulation/schemas'

// スプレッドの注文の脚ごとの手数料。単価は円/kWh
export type FeeLegResultModel = {
  legType: 'base' | 'exchange'
  // その脚だけの要約(エリア・量・価格)
  orderSummary: string
  // その脚の単価。基準側 = eClear 利用料 + 金利コスト、交換側 = 交換側の eClear 利用料 + 交換側の金利コスト(いつも 0)
  fee: string
}

export type FeeResultModel = {
  // 合算の単価。バックエンドが出した値をそのまま出す(脚ごとの単価を画面で足し直さない)
  fee: string
  orderNumber: number
  orderSummary: string
  // 手数料を表示しない。非開示かどうかの判定はバックエンドが行う
  isFeeHidden: boolean
  // 脚ごとの手数料(基準側 → 交換側)。スプレッドの注文で、非開示でなく、両脚の単価が 0 より大きいときだけ入る
  legFees: FeeLegResultModel[] | undefined
}

function toDecimal(value: string | undefined): Decimal {
  if (value === undefined || value === '') {
    return new Decimal(0)
  }
  return new Decimal(value)
}

type SummaryTransaction = Parameters<typeof createOrderSummaryText>[0]['base']

function toSummaryTransaction(
  order: SimulationOrderPatternModel,
  summaryOrder: TransactionSchema,
  position: DealPosition,
): SummaryTransaction {
  return {
    position,
    areaCode: summaryOrder.areaCode,
    deliveryPeriodType: summaryOrder.deliveryPeriodType,
    deliveryDateRange: summaryOrder.deliveryDateRange,
    deliveryHourType: summaryOrder.deliveryHourType,
    volumeInKw: summaryOrder.volumeInKw,
    unitPricePerKwh: summaryOrder.unitPricePerKwh,
    selfDeliveryType: order.selfDeliveryType,
    counterpartyDeliveryType: order.eClearDeliveryType,
    isPhysicalFuture: order.isPhysicalFuture,
    floatingIndexCode: summaryOrder.floatingIndexCode,
  }
}

// 交換側の脚は基準側と反対の売買になる(バックエンドへ送る脚と同じ)
function oppositePosition(position: DealPosition): DealPosition {
  return position === DealPosition.BID ? DealPosition.OFFER : DealPosition.BID
}

// スプレッドの注文の脚ごとの手数料を作る。
// スプレッドでない注文・非開示の注文・交換側の脚が無い注文・どちらかの脚の単価が 0 の注文では作らない(合算だけ出す)。
export function convertFeeLegResultModels(
  orderFee: OrderFeeResult,
  order: SimulationOrderPatternModel,
): FeeLegResultModel[] | undefined {
  if (orderFee.isFeeHidden || !order.spreadType.isSpreadTrade) {
    return undefined
  }
  const { exchangeSummaryOrder } = order
  if (exchangeSummaryOrder === undefined) {
    return undefined
  }

  const baseFee = toDecimal(orderFee.eclearFeeRate?.value).add(
    toDecimal(orderFee.interestCharge?.value),
  )
  // 交換側の金利コストは業務上いつも 0 なので、交換側の eClear 利用料だけ。
  // 応答に交換側の値が無い(現現・flag OFF)ときは 0
  const exchangeFee = toDecimal(orderFee.exchangeEclearFeeRate?.value)
  // どちらかの脚の単価が 0 なら内訳は出さない(合算だけ出す)。
  // flag OFF・現現は交換側が 0 になるので、今までどおりの表示になる
  if (!baseFee.greaterThan(0) || !exchangeFee.greaterThan(0)) {
    return undefined
  }

  return [
    {
      legType: 'base',
      orderSummary: createOrderSummaryText({
        isSpread: false,
        base: toSummaryTransaction(
          order,
          order.baseSummaryOrder,
          order.dealPosition,
        ),
      }).value,
      fee: baseFee.toFixed(),
    },
    {
      legType: 'exchange',
      orderSummary: createOrderSummaryText({
        isSpread: false,
        base: toSummaryTransaction(
          order,
          exchangeSummaryOrder,
          oppositePosition(order.dealPosition),
        ),
      }).value,
      fee: exchangeFee.toFixed(),
    },
  ]
}

export function convertFeeResultModels(
  response: SimulateOrdersResponse,
  orders: SimulationOrderPatternModel[],
): FeeResultModel[] {
  return response.outputs[0].orderFees.map((orderFee) => {
    const targetOrder = orders.find(
      (order) => order.orderNumber === orderFee.orderNumber,
    )
    // TODO: どう処理するか考える
    if (targetOrder === undefined) {
      return {
        fee: '0',
        orderNumber: orderFee.orderNumber,
        orderSummary: '-',
        isFeeHidden: orderFee.isFeeHidden,
        legFees: undefined,
      }
    }
    return {
      fee: orderFee?.feePerKwh?.value.toLocaleString() ?? '0',
      orderNumber: orderFee.orderNumber,
      isFeeHidden: orderFee.isFeeHidden,
      legFees: convertFeeLegResultModels(orderFee, targetOrder),
      orderSummary: createOrderSummaryText({
        isSpread: targetOrder.spreadType.isSpreadTrade,
        base: toSummaryTransaction(
          targetOrder,
          targetOrder.baseSummaryOrder,
          targetOrder.dealPosition,
        ),
        exchange:
          targetOrder.exchangeSummaryOrder !== undefined
            ? toSummaryTransaction(
                targetOrder,
                targetOrder.exchangeSummaryOrder,
                targetOrder.dealPosition,
              )
            : undefined,
      }).value,
    }
  })
}
